/**
 * Utility functions for handling client configuration logic
 **/

import { requiredRule, valueInItemsRule } from "@/utils/form";

/** Transform keys to match config/JSON serialization (lowercase first letter, remove spaces). Used when matching labels to config keys. */
export const keyForConfigMatch = (key) => {
  if (typeof key !== "string") return key;
  return key.charAt(0).toLowerCase() + key.slice(1).replace(/\s+/g, "");
};

/**
 * Get medication defaults for a medication name from priorAuth.medicationDefaults.
 * @param {string} medicationName - Medication name (e.g. from autocomplete or intake)
 * @param {Object} medicationDefaultsMap - requirementsStore.clientConfig?.priorAuth?.medicationDefaults
 * @returns {Object|null} - Matched defaults object or null
 */
export const getMedicationDefaultsForName = (medicationName, medicationDefaultsMap) => {
  if (!medicationDefaultsMap || typeof medicationDefaultsMap !== "object") return null;
  const nameKey = keyForConfigMatch((medicationName ?? "").toString().trim());
  if (!nameKey) return null;
  const matchKey = Object.keys(medicationDefaultsMap).find((k) => k === nameKey);
  return matchKey ? medicationDefaultsMap[matchKey] : null;
};

/**
 * Apply medication defaults onto a target object. Optionally call emitUpdate(field, value) for each applied field.
 * @param {Object} defaultValues - Result from getMedicationDefaultsForName
 * @param {Object} target - Object to mutate (e.g. orderInfo or extraMed)
 * @param {function} [emitUpdate] - Optional (field, value) => void, called for each applied field
 */
export const applyMedicationDefaultsToTarget = (defaultValues, target, emitUpdate = () => {}) => {
  if (!defaultValues || typeof defaultValues !== "object") return;
  Object.entries(defaultValues).forEach(([field, value]) => {
    if (value != null && value !== "") {
      target[field] = value;
      emitUpdate(field, value);
    }
  });
};

/**
 * Apply client configuration defaults to an object's fields
 * @param {Object} valueObject - The object to apply defaults to
 * @param {Array} fields - Array of field definitions
 * @param {Object} clientConfig - Client configuration object
 * @param {function} emitCallback - Function to update object field
 * @returns {Object} - Updated value object with defaults applied
 */
export const applyClientConfigDefaults = (valueObject, fields, clientConfig, emitCallback) => {
  if (!clientConfig?.intake?.defaultFields) {
    return valueObject;
  }

  const intakeConfig = clientConfig.intake;

  // Set default values on the value object if the field is null/undefined (never set)
  // Do not override explicitly set empty strings (user intentionally cleared the field)
  Object.keys(intakeConfig.defaultFields).forEach((fieldKey) => {
    const isExtraField = intakeConfig.extraFields?.includes(fieldKey);
    const currentValue = isExtraField ? valueObject.extraData?.[fieldKey] ?? null : valueObject[fieldKey];

    if ((currentValue === null || currentValue === undefined) && fields.some((field) => field.key === fieldKey)) {
      const defaultValue = intakeConfig.defaultFields[fieldKey];
      if (isExtraField && !valueObject.hasOwnProperty(fieldKey)) {
        if (!valueObject.extraData) {
          valueObject.extraData = {};
        }
        valueObject.extraData[fieldKey] = defaultValue;
      } else {
        valueObject[fieldKey] = defaultValue;
      }
      emitCallback(defaultValue, { key: fieldKey });
    }
  });

  return valueObject;
};

/**
 * Filter fields based on client configuration (hide fields, extra fields, etc.)
 * @param {Array} fields - Array of field definitions
 * @param {Object} clientConfig - Client configuration object
 * @param {string} documentType - Type of document (INSURANCE_CARD or ORDER_FORM)
 * @returns {Array} - Filtered array of fields
 */
export const filterFieldsByClientConfig = (fields, clientConfig) => {
  if (!clientConfig?.intake) {
    return fields;
  }

  const intakeConfig = clientConfig.intake;
  let filteredFields = [...fields];

  filteredFields = filteredFields.filter(
    (field) => !field.isExtraField || intakeConfig.extraFields?.includes(field.key)
  );

  // Filter out hidden fields
  if (intakeConfig.hideFields) {
    filteredFields = filteredFields.filter((field) => !intakeConfig.hideFields.includes(field.key));
  }

  return filteredFields;
};

/**
 * Apply field transformations based on client configuration (rename, required rules)
 * @param {Array} fields - Array of field definitions
 * @param {Object} clientConfig - Client configuration object
 * @returns {Array} - Transformed array of fields
 */
export const transformFieldsByClientConfig = (fields, clientConfig) => {
  if (!clientConfig?.intake) {
    return fields;
  }

  const intakeConfig = clientConfig.intake;
  let transformedFields = [...fields];

  // Apply field renaming
  if (intakeConfig.renameFields) {
    transformedFields = transformedFields.map((field) => ({
      ...field,
      label: intakeConfig.renameFields[field.key] || field.label,
    }));
  }

  // Apply required field rules
  if (intakeConfig.requiredFields) {
    transformedFields = transformedFields.map((field) => {
      if (intakeConfig.requiredFields.includes(field.key)) {
        // Check if field has a component with vBind.rules
        if (field.cmp?.vBind) {
          const existingRules = field.cmp.vBind.rules || [];
          const hasRequiredRule = existingRules.some(
            (rule) => rule.name === "required" || rule.toString().includes("required")
          );
          if (!hasRequiredRule) {
            return {
              ...field,
              cmp: {
                ...field.cmp,
                vBind: {
                  ...field.cmp.vBind,
                  rules: [requiredRule, ...existingRules],
                },
              },
            };
          }
        } else {
          // Field-level rules
          const existingRules = field.rules || [];
          const hasRequiredRule = existingRules.some(
            (rule) => rule.name === "required" || rule.toString().includes("required")
          );
          if (!hasRequiredRule) {
            return {
              ...field,
              rules: [requiredRule, ...existingRules],
            };
          }
        }
      }
      return field;
    });
  }

  // Add empty option to all v-select components
  transformedFields = transformedFields.map((field) => {
    if (field.cmp?.component === "v-select" && field.cmp?.vBind?.items) {
      const items = field.cmp.vBind.items;
      // Check if empty option already exists
      const hasEmptyOption = items.some((item) => item.value === "");

      if (!hasEmptyOption) {
        const emptyOption = { title: field.label, value: "" };
        return {
          ...field,
          cmp: {
            ...field.cmp,
            vBind: {
              ...field.cmp.vBind,
              items: [emptyOption, ...items],
            },
          },
        };
      }
    }
    return field;
  });

  return transformedFields;
};

/**
 * Apply dropdown field transformations based on client configuration
 * @param {Array} fields - Array of field definitions
 * @param {Object} clientConfig - Client configuration object
 * @returns {Array} - Transformed array of fields with dropdown configurations
 */
export const transformDropdownFieldsByClientConfig = (fields, clientConfig) => {
  if (!clientConfig?.intake?.dropdownFields) {
    return fields;
  }

  const dropdownFields = clientConfig.intake.dropdownFields;
  let transformedFields = [...fields];

  // Apply dropdown field configurations
  transformedFields = transformedFields.map((field) => {
    if (dropdownFields[field.key]) {
      const dropdownOptions = dropdownFields[field.key];

      // If field already has a cmp, merge with it; otherwise create new cmp
      if (field.cmp) {
        // If already has v-combobox component, merge items; otherwise replace component
        if (field.cmp.component === "v-combobox") {
          return {
            ...field,
            cmp: {
              ...field.cmp,
              vBind: {
                ...field.cmp.vBind,
                items: dropdownOptions,
                rules: [(v) => valueInItemsRule(v, dropdownOptions), ...(field.cmp.vBind?.rules || [])],
              },
            },
          };
        } else {
          // Replace existing component with v-combobox
          return {
            ...field,
            cmp: {
              ...field.cmp,
              component: "v-combobox",
              vBind: {
                ...field.cmp.vBind,
                items: dropdownOptions,
                rules: [(v) => valueInItemsRule(v, dropdownOptions), ...(field.cmp.vBind?.rules || [])],
              },
            },
          };
        }
      } else {
        // Create new cmp with v-combobox component
        return {
          ...field,
          cmp: {
            component: "v-combobox",
            vBind: {
              items: dropdownOptions,
              rules: [(v) => valueInItemsRule(v, dropdownOptions)],
            },
          },
        };
      }
    }
    return field;
  });

  return transformedFields;
};

/**
 * Apply conditional field logic based on client configuration
 */
export const applyConditionalFieldLogic = (
  valueObject,
  changedFieldKey,
  changedFieldTitle,
  clientConfig,
  emitCallback
) => {
  if (!clientConfig?.intake?.conditionalFields?.[changedFieldKey]) {
    return;
  }

  const conditions = clientConfig.intake.conditionalFields[changedFieldKey];
  const dropdownFields = clientConfig.intake.dropdownFields || {};

  // Find matching condition
  let conditionMatch = null;

  if (typeof changedFieldTitle === "string") {
    // String values: try both original and transformed keys
    const transformedKey = keyForConfigMatch(changedFieldTitle);
    conditionMatch = conditions[changedFieldTitle] || conditions[transformedKey];
  } else if (typeof changedFieldTitle === "object" && changedFieldTitle !== null) {
    // Object/array values: check for "key:value" patterns
    const checkObjectMatch = (obj, objectKey, objectValue) => {
      if (obj && typeof obj === "object" && obj[objectKey] !== undefined) {
        const actualValue = String(obj[objectKey]).toLowerCase();
        const transformedValue = String(keyForConfigMatch(obj[objectKey])).toLowerCase();
        const targetValue = String(objectValue).toLowerCase();
        return actualValue.includes(targetValue) || transformedValue.includes(targetValue);
      }
      return false;
    };

    for (const conditionKey of Object.keys(conditions)) {
      if (conditionKey.includes(":")) {
        const [objectKey, objectValue] = conditionKey.split(":", 2);
        let matchFound = false;

        if (Array.isArray(changedFieldTitle)) {
          matchFound = changedFieldTitle.some((item) => checkObjectMatch(item, objectKey, objectValue));
        } else {
          matchFound = checkObjectMatch(changedFieldTitle, objectKey, objectValue);
        }

        if (matchFound) {
          conditionMatch = conditions[conditionKey];
          break;
        }
      }
    }
  }

  if (!conditionMatch) {
    return;
  }

  // Apply conditional values to target fields
  Object.entries(conditionMatch).forEach(([targetFieldKey, targetFieldValue]) => {
    let finalValue = targetFieldValue;

    // For dropdown fields, find the corresponding option value
    if (dropdownFields[targetFieldKey]) {
      const dropdownOption = dropdownFields[targetFieldKey].find((option) => option.title === targetFieldValue);
      if (dropdownOption) {
        finalValue = dropdownOption.value;
      }
    }

    // Apply the final value
    if (
      typeof valueObject[targetFieldKey] === "object" &&
      valueObject[targetFieldKey] !== null &&
      typeof finalValue === "string" &&
      finalValue.includes(":")
    ) {
      // Update object property using "key:value" pattern
      const [objectKey, objectValue] = finalValue.split(":", 2);

      if (Array.isArray(valueObject[targetFieldKey])) {
        // Update array object property
        for (const item of valueObject[targetFieldKey]) {
          if (item && typeof item === "object" && item[objectKey] !== undefined) {
            const currentValue = String(item[objectKey]).toLowerCase();
            const targetValue = String(objectValue).toLowerCase();
            if (currentValue !== targetValue) {
              const updatedArray = valueObject[targetFieldKey].map((arrayItem) =>
                arrayItem === item ? { ...arrayItem, [objectKey]: objectValue } : arrayItem
              );
              emitCallback(updatedArray, { key: targetFieldKey });
              break;
            }
          }
        }
      } else {
        // Update single object property
        const currentValue = String(valueObject[targetFieldKey][objectKey]).toLowerCase();
        const targetValue = String(objectValue).toLowerCase();
        if (currentValue !== targetValue) {
          const updatedObject = { ...valueObject[targetFieldKey], [objectKey]: objectValue };
          emitCallback(updatedObject, { key: targetFieldKey });
        }
      }
    } else {
      // Replace entire field value
      if (valueObject[targetFieldKey] !== finalValue) {
        emitCallback(finalValue, { key: targetFieldKey });
      }
    }
  });
};
